/** Publisher dashboard — listings, this month's money, and each listing's
 *  free-trial budget (topped up, used, set aside, available). */
import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  budgetAuto, budgetCheckout, budgetFromWallet, budgetWithdraw, fmtDate, getSellerDashboard,
  money, mktError, pauseListing, type Listing, type SellerCustomer,
} from '@/api/marketplace'
import { useRazorpayCheckout } from '@/hooks/useRazorpayCheckout'
import MarketplaceShell, { AppTile } from './MarketplaceShell'

type Dash = Awaited<ReturnType<typeof getSellerDashboard>>

const LISTING_PILL: Record<string, [string, string]> = {
  draft: ['Draft', ''], in_review: ['In review', 'info'], changes_requested: ['Changes requested', 'warn'],
  live: ['Live', 'ok'], paused: ['Paused', 'warn'], removed: ['Removed', 'bad'],
}

export default function SellerDashboard() {
  const [dash, setDash] = useState<Dash | null>(null)
  const [error, setError] = useState<string | null>(null)
  const load = useCallback(() => {
    getSellerDashboard().then(setDash).catch((e) => setError(mktError(e, "We couldn't load your listings.").message))
  }, [])
  useEffect(load, [load])

  const alerts = (dash?.listings || []).filter((x) => x.listing.pricing.trial.days > 0 && ['low', 'empty'].includes(x.listing.trial_budget.level))
  const anyCurrency = dash?.listings[0]?.listing.currency || 'INR'
  const totals = dash?.month_totals[anyCurrency]
  const customers = (dash?.listings || []).flatMap((x) => x.customers)
  const paying = customers.filter((c) => c.status === 'active' || c.status === 'past_due').length
  const trials = customers.filter((c) => c.status === 'trial').length

  return (
    <MarketplaceShell sell>
      <div className="mkt-wrap mkt-col" style={{ padding: '24px 20px 48px', gap: 22 }}>
        <div className="mkt-row" style={{ justifyContent: 'space-between' }}>
          <div className="mkt-col" style={{ gap: 2 }}><h1 style={{ fontSize: 28 }}>Your listings &amp; earnings</h1><span className="mkt-muted">{new Date().toLocaleDateString(undefined, { month: 'long', year: 'numeric' })} so far</span></div>
          <Link to="/marketplace/sell" className="btn btn-primary">Publish an app</Link>
        </div>
        {error && <div className="mkt-banner bad"><div>{error}</div></div>}

        {alerts.map(({ listing }) => (
          <div key={listing.id} role="alert" className={`mkt-banner ${listing.trial_budget.level === 'empty' ? 'bad' : 'warn'}`}>
            <div>
              <strong>{listing.trial_budget.level === 'empty' ? `Free trials are paused for ${listing.name}` : `${listing.name}: trial budget is running low`}</strong>
              {listing.trial_budget.level === 'empty'
                ? `The budget has ${money(listing.trial_budget.available_minor, listing.currency)} left — not enough for one more trial (${money(listing.trial_budget.per_trial_minor, listing.currency)}). New customers now see no free trial and pay from day one.${listing.trial_budget.running ? ` The ${listing.trial_budget.running} trial${listing.trial_budget.running === 1 ? '' : 's'} already running will finish normally.` : ''}`
                : `Only enough for ${listing.trial_budget.trials_left} more trial${listing.trial_budget.trials_left === 1 ? '' : 's'}. When it can't cover one more, free trials stop.`}
            </div>
            <a href={`#budget-${listing.id}`} className="btn btn-primary">Top up trial budget</a>
          </div>
        ))}

        {dash && dash.listings.length === 0 && (
          <div className="mkt-empty"><strong style={{ fontSize: 18 }}>No listings yet</strong><span className="mkt-muted">Publish one of your live apps and it shows up here.</span><Link to="/marketplace/sell" className="btn btn-primary">Publish an app</Link></div>
        )}

        {dash && dash.listings.length > 0 && (
          <div className="mkt-kpis">
            <div className="mkt-dark mkt-col" style={{ gap: 4 }}><span className="mkt-muted">You earn this month</span><strong style={{ fontSize: 28 }}>{money(totals?.earn ?? 0, anyCurrency)}</strong><span className="mkt-muted" style={{ fontSize: 13 }}>Paid into your app wallet as customers pay</span></div>
            <div className="mkt-kpi"><span className="mkt-muted">Paying customers</span><strong>{paying}</strong><span className="mkt-faint">+ {trials} on a free trial</span></div>
            <div className="mkt-kpi"><span className="mkt-muted">People using your apps</span><strong>{customers.filter((c) => ['trial', 'active', 'past_due'].includes(c.status)).reduce((a, c) => a + c.people, 0)}</strong><span className="mkt-faint">at customer organizations</span></div>
            <div className="mkt-kpi"><span className="mkt-muted">Fee &amp; notice</span><strong style={{ fontSize: 20 }}>{dash.fee_percent}% fee</strong><span className="mkt-faint">Price changes reach existing customers after {dash.notice_days} days</span></div>
          </div>
        )}

        {dash?.listings.map((x) => <ListingBlock key={x.listing.id} item={x} onChanged={load} />)}
      </div>
    </MarketplaceShell>
  )
}

function ListingBlock({ item, onChanged }: { item: Dash['listings'][number]; onChanged: () => void }) {
  const l = item.listing
  const [pill, cls] = LISTING_PILL[l.status] || [l.status, '']
  const [busy, setBusy] = useState(false)
  const togglePause = async () => {
    setBusy(true)
    try { await pauseListing(l.id, l.status === 'live'); onChanged() } catch (e) { toast.error(mktError(e).message) } finally { setBusy(false) }
  }
  const cur = l.currency
  const month = item.customers.reduce((a, c) => ({ amount: a.amount + c.month_paid_minor, hosting: a.hosting + c.month_hosting_minor, fee: a.fee + c.month_fee_minor, earn: a.earn + c.month_earn_minor }), { amount: 0, hosting: 0, fee: 0, earn: 0 })
  return (
    <section className="mkt-panel mkt-col" style={{ gap: 18 }} aria-label={l.name || l.project}>
      <div className="mkt-row" style={{ justifyContent: 'space-between' }}>
        <div className="mkt-row" style={{ gap: 14, flexWrap: 'nowrap' }}>
          <AppTile color={l.icon_color} text={(l.name || l.project).split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase()} />
          <div className="mkt-col" style={{ gap: 2 }}>
            <div className="mkt-row" style={{ gap: 8 }}><strong style={{ fontSize: 19 }}>{l.name || item.title}</strong><span className={`mkt-pill ${cls}`}>{pill}</span></div>
            <span className="mkt-muted" style={{ fontSize: 14 }}>{priceLine(l)}</span>
          </div>
        </div>
        <div className="mkt-row">
          {l.status === 'live' && <Link to={l.store_url} className="btn btn-ghost" target="_blank">Store page</Link>}
          <Link to={`/marketplace/sell/${encodeURIComponent(l.project)}?owner=${encodeURIComponent(l.owner_user_id)}`} className="btn btn-secondary">Edit listing</Link>
          {(l.status === 'live' || l.status === 'paused') && <button type="button" className="btn btn-secondary" disabled={busy} onClick={togglePause}>{l.status === 'live' ? 'Pause' : 'Resume'}</button>}
        </div>
      </div>
      {l.status === 'changes_requested' && l.review_note && <div className="mkt-banner warn"><div><strong>Reviewer's note</strong>{l.review_note}</div></div>}

      {l.pricing.trial.days > 0 && <TrialBudgetCard listing={l} appWallet={item.app_wallet_minor} onChanged={onChanged} customers={item.customers} />}

      {item.customers.length > 0 && (
        <div className="mkt-col" style={{ gap: 10 }}>
          <h2>Where this month's money goes</h2>
          {month.amount > 0 && (
            <>
              <div className="mkt-stack" style={{ height: 36 }} role="img" aria-label={`Of ${money(month.amount, cur)}: hosting ${money(month.hosting, cur)}, fee ${money(month.fee, cur)}, you ${money(month.earn, cur)}`}>
                <span style={{ flex: month.hosting, background: '#3A404C' }} /><span style={{ flex: Math.max(month.fee, 1), background: '#C9CED6' }} /><span style={{ flex: Math.max(month.earn, 0), background: 'var(--color-success)' }} />
              </div>
              <span className="mkt-faint">Customers paid {money(month.amount, cur)} · Hosting {money(month.hosting, cur)} · Fee {money(month.fee, cur)} · You earn {money(month.earn, cur)}</span>
            </>
          )}
          <div className="mkt-scroll">
            <table className="mkt-table" style={{ minWidth: 720 }}>
              <thead><tr><th scope="col">Customer</th><th scope="col">Plan</th><th scope="col">Status</th><th scope="col" className="num">They paid</th><th scope="col" className="num">Hosting</th><th scope="col" className="num">Fee</th><th scope="col" className="num">You earn</th></tr></thead>
              <tbody>
                {item.customers.map((c) => (
                  <tr key={c.id}>
                    <td><strong>{c.organization}</strong><br /><span className="mkt-faint">Since {fmtDate(c.since)}</span></td>
                    <td>{c.plan}</td>
                    <td>{custStatus(c)}</td>
                    <td className="num">{money(c.month_paid_minor, cur)}</td>
                    <td className="num">{c.status === 'trial' ? 'Trial budget' : money(c.month_hosting_minor, cur)}</td>
                    <td className="num">{money(c.month_fee_minor, cur)}</td>
                    <td className="num" style={{ fontWeight: 700, color: 'var(--color-success)' }}>{money(c.month_earn_minor, cur)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <span className="mkt-faint">App wallet: <strong style={{ color: 'var(--color-text)' }}>{money(item.app_wallet_minor, item.app_wallet_currency)}</strong> — your earnings land here; hosting for your own organization and trial top-ups can be paid from it.</span>
    </section>
  )
}

function custStatus(c: SellerCustomer) {
  const m: Record<string, [string, string]> = { trial: ['Free trial', 'info'], active: ['Paying', 'ok'], past_due: ['Payment overdue', 'bad'], ended: ['Ended', ''], deleted: ['Removed', ''], installing: ['Setting up', 'info'] }
  const [t, cls] = m[c.status] || [c.status, '']
  return <span className={`mkt-pill ${cls}`}>{t}{c.status === 'trial' && c.trial_ends_at ? ` · until ${fmtDate(c.trial_ends_at)}` : ''}</span>
}

function priceLine(l: Listing): string {
  const parts: string[] = []
  if (l.pricing.person.enabled && l.pricing.person.price_minor) parts.push(`${money(l.pricing.person.price_minor, l.currency)} / person`)
  const servers = Object.values(l.pricing.server).filter((o) => o.enabled && o.price_minor).map((o) => o.price_minor as number)
  if (servers.length) parts.push(`from ${money(Math.min(...servers), l.currency)} / server`)
  parts.push(l.pricing.trial.days ? (l.trial_budget.level === 'empty' ? 'free trial paused' : `${l.pricing.trial.days}-day free trial`) : 'no free trial')
  return parts.join(' · ')
}

function TrialBudgetCard({ listing, appWallet, onChanged, customers }: { listing: Listing; appWallet: number; onChanged: () => void; customers: SellerCustomer[] }) {
  const b = listing.trial_budget
  const cur = listing.currency
  const [open, setOpen] = useState(false)
  const [trials, setTrials] = useState(b.topup_options.find((o) => o.trials === 10)?.trials ?? b.topup_options[0]?.trials ?? b.min_topup_trials)
  const [busy, setBusy] = useState(false)
  const { checkout, busy: paying } = useRazorpayCheckout()
  const total = Math.max(1, b.used_minor + b.held_minor + b.available_minor)
  const color = b.level === 'empty' ? 'var(--color-error)' : b.level === 'low' ? 'var(--color-warning)' : 'var(--color-success)'
  const amount = trials * b.per_trial_minor

  const fromWallet = async () => {
    setBusy(true)
    try { await budgetFromWallet(listing.id, trials); toast.success('Trial budget topped up'); setOpen(false); onChanged() } catch (e) { toast.error(mktError(e).message) } finally { setBusy(false) }
  }
  const withCard = async () => {
    const r = await checkout({ purpose: 'trial_budget_topup', description: 'Free-trial budget', precreate: () => budgetCheckout(listing.id, trials) })
    if (r.cancelled) return
    if (!r.ok) { toast.error(r.error || 'The payment did not go through.'); return }
    toast.success('Trial budget topped up — trials restart right away')
    setOpen(false)
    onChanged()
  }
  const toggleAuto = async () => {
    try { await budgetAuto(listing.id, !b.auto_topup, b.auto_topup_trials ?? trials); onChanged() } catch (e) { toast.error(mktError(e).message) }
  }
  const withdraw = async () => {
    try { const r = await budgetWithdraw(listing.id); toast.success(r.moved_minor ? `${money(r.moved_minor, cur)} moved to your app wallet` : 'Nothing to move'); onChanged() } catch (e) { toast.error(mktError(e).message) }
  }
  const trialRows = customers.filter((c) => c.trial_reserved_minor > 0)
  const finished = trialRows.filter((c) => c.trial_released)
  const won = finished.filter((c) => c.converted).length

  return (
    <div id={`budget-${listing.id}`} className="mkt-panel mkt-col" style={{ gap: 14, background: 'var(--color-bg)' }}>
      <div className="mkt-row" style={{ justifyContent: 'space-between' }}>
        <div className="mkt-row" style={{ gap: 10 }}>
          <h2>Free-trial budget</h2>
          <span className={`mkt-pill ${b.level === 'empty' ? 'bad' : b.level === 'low' ? 'warn' : 'ok'}`}>{b.level === 'empty' ? 'Free trials paused' : b.level === 'low' ? 'Running low' : 'Healthy'}</span>
        </div>
        <button type="button" className="btn btn-primary" onClick={() => setOpen(!open)}>Top up</button>
      </div>
      <div className="mkt-stack" role="img" aria-label={`Used ${money(b.used_minor, cur)}, set aside ${money(b.held_minor, cur)}, available ${money(b.available_minor, cur)}`}>
        <span style={{ width: `${(b.used_minor / total) * 100}%`, background: '#3A404C' }} />
        <span style={{ width: `${(b.held_minor / total) * 100}%`, background: '#6C8FE0' }} />
        <span style={{ width: `${(b.available_minor / total) * 100}%`, background: color }} />
      </div>
      <div className="mkt-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
        <Figure label="Topped up so far" value={money(b.topped_up_minor, cur)} note="Paid in advance by you" />
        <Figure label="Used by trials" value={money(b.used_minor, cur)} note="Hosting for days customers actually tried it" dot="#3A404C" />
        <Figure label="Set aside for running trials" value={money(b.held_minor, cur)} note={`${b.running} running · what's left comes back when they end`} dot="#6C8FE0" />
        <Figure label="Available" value={money(b.available_minor, cur)} note={b.level === 'empty' ? `Less than one trial (${money(b.per_trial_minor, cur)}) — new trials are paused` : `Enough for ${b.trials_left} more trials`} dot={color} strong={color} />
      </div>
      <div className="mkt-row" style={{ justifyContent: 'space-between', background: 'var(--color-bg-secondary)', borderRadius: 12, padding: '10px 14px', fontSize: 14 }}>
        <span>On your store page: <strong style={{ color: b.level === 'empty' ? 'var(--color-error)' : 'var(--color-success)' }}>{b.level === 'empty' ? 'No free trial shown — new customers pay from day one' : `“${listing.pricing.trial.days}-day free trial” is shown`}</strong></span>
        <span className="mkt-faint">One trial = up to {listing.pricing.trial.people} people × {listing.pricing.trial.days} days = {money(b.per_trial_minor, cur)}</span>
      </div>

      {open && (
        <div className="mkt-panel mkt-panel-accent mkt-col">
          <strong style={{ fontSize: 17 }}>Top up the trial budget</strong>
          <div className="mkt-choices" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }} role="radiogroup" aria-label="Top-up amount">
            {b.topup_options.map((o) => (
              <button key={o.trials} type="button" role="radio" aria-checked={trials === o.trials} className={`mkt-choice${trials === o.trials ? ' on' : ''}`} onClick={() => setTrials(o.trials)}>
                <strong style={{ fontSize: 19 }}>{money(o.amount_minor, cur)}</strong><span className="mkt-muted" style={{ fontSize: 14 }}>{o.trials} more trials</span>
              </button>
            ))}
          </div>
          <div className="mkt-row">
            <button type="button" className="btn btn-primary" disabled={paying} onClick={withCard}>Pay {money(amount, cur)} with UPI or card</button>
            <button type="button" className="btn btn-secondary" disabled={busy || appWallet < amount} onClick={fromWallet} title={appWallet < amount ? 'Your app wallet is too low' : undefined}>Pay from app wallet ({money(appWallet, cur)})</button>
          </div>
          <span className="mkt-faint">Trials restart the moment the payment goes through.</span>
        </div>
      )}

      <div className="mkt-row" style={{ justifyContent: 'space-between' }}>
        <button type="button" role="switch" aria-checked={b.auto_topup} onClick={toggleAuto} className="mkt-choice mkt-choice-row" style={{ justifyContent: 'flex-start', maxWidth: 560 }}>
          <span className={`mkt-switch${b.auto_topup ? ' on' : ''}`} aria-hidden="true" />
          <span className="mkt-col" style={{ gap: 0 }}><strong style={{ fontSize: 15 }}>Top up automatically</strong><span className="mkt-muted" style={{ fontSize: 14 }}>Add {b.auto_topup_trials ?? b.min_topup_trials} trials from your app wallet whenever {b.low_at_trials} or fewer are left, so trials never pause.</span></span>
        </button>
        {b.available_minor > 0 && <button type="button" className="btn btn-ghost" onClick={withdraw}>Move unused budget to app wallet</button>}
      </div>

      {trialRows.length > 0 && (
        <div className="mkt-scroll">
          <table className="mkt-table" style={{ minWidth: 640 }}>
            <caption style={{ textAlign: 'left', fontWeight: 700, paddingBottom: 6 }}>Trials so far</caption>
            <thead><tr><th scope="col">Organization</th><th scope="col">When</th><th scope="col" className="num">Used</th><th scope="col" className="num">Set aside</th><th scope="col">Outcome</th></tr></thead>
            <tbody>
              {trialRows.map((c) => (
                <tr key={c.id}>
                  <td><strong>{c.organization}</strong></td>
                  <td>{c.status === 'trial' ? `Until ${fmtDate(c.trial_ends_at)}` : `Ended ${fmtDate(c.trial_ends_at)}`}</td>
                  <td className="num">{money(c.trial_used_minor, cur)}</td>
                  <td className="num">{c.trial_released ? '—' : money(Math.max(0, c.trial_reserved_minor - c.trial_used_minor), cur)}</td>
                  <td><span className={`mkt-pill ${c.status === 'trial' ? 'info' : c.converted ? 'ok' : ''}`}>{c.status === 'trial' ? 'Running' : c.converted ? 'Became a customer' : 'Ended — didn’t buy'}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
          {finished.length > 0 && <span className="mkt-faint">{won} of {finished.length} finished trials became paying customers.</span>}
        </div>
      )}
    </div>
  )
}

function Figure({ label, value, note, dot, strong }: { label: string; value: string; note: string; dot?: string; strong?: string }) {
  return (
    <div className="mkt-col" style={{ gap: 2 }}>
      <span className="mkt-muted mkt-row" style={{ gap: 6, fontSize: 14 }}>{dot && <span aria-hidden="true" style={{ width: 11, height: 11, borderRadius: 3, background: dot }} />}{label}</span>
      <strong style={{ fontSize: 20, color: strong }}>{value}</strong>
      <span className="mkt-faint">{note}</span>
    </div>
  )
}
